// The top bar's timer: Foundation's TimeTrackerButton on the caller's own
// punches from the last 14 days (apps/server/src/timer.mjs). Loads on mount,
// so the bubble shows a running timer, each time it opens, when the tab comes
// back into view and when another tab changes the timer.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TimeTrackerButton, type TimerPunch } from '@olytic/foundation/timekeeping';
import { authenticatedFetch } from '../auth/authClient';
import { ObjectsApiError } from '../objects/objectsApi';
import { deletePunch, fetchTimer, startTimer, stopTimer, updatePunch, type TimerData } from './workApi';

const DAYS = 14;
const CHANNEL = 'mothership-timer';

/** Local midnight 13 days back to local midnight tomorrow, as ISO times. */
function windowNow(): [string, string] {
  const from = new Date();
  from.setHours(0, 0, 0, 0);
  from.setDate(from.getDate() - (DAYS - 1));
  const to = new Date();
  to.setHours(0, 0, 0, 0);
  to.setDate(to.getDate() + 1);
  return [from.toISOString(), to.toISOString()];
}

export function TopBarTimer() {
  const [data, setData] = useState<TimerData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hidden, setHidden] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fetchTimer(authenticatedFetch, ...windowNow()));
      setError(null);
    } catch (cause) {
      // No person record (a break-glass sign-in): no timer to show.
      if (cause instanceof ObjectsApiError && cause.status === 403) setHidden(true);
      else setError(cause instanceof Error ? cause.message : 'Unable to load your time.');
    } finally {
      setLoading(false);
    }
  }, []);

  // The timer lives in the database, so every tab shows the same one: each
  // tab reloads when it comes back into view, and tells the others when it
  // starts, stops or edits something.
  const channel = useMemo(() => (typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL)), []);
  useEffect(() => {
    void load();
    const onVisible = () => {
      if (document.visibilityState === 'visible') void load();
    };
    const onMessage = () => void load();
    document.addEventListener('visibilitychange', onVisible);
    channel?.addEventListener('message', onMessage);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      channel?.removeEventListener('message', onMessage);
    };
  }, [channel, load]);
  useEffect(() => () => channel?.close(), [channel]);

  const punches = useMemo<TimerPunch[]>(
    () => (data?.punches ?? []).map((p) => ({
      id: p.id,
      projectId: p.project_id,
      startedAt: p.started_at,
      endedAt: p.ended_at,
      project: { id: p.project_id, code: p.project_code, name: p.project_name },
    })),
    [data],
  );
  const projects = useMemo(() => (data?.projects ?? []).map((p) => ({ id: p.id, code: p.code, name: p.name })), [data]);

  // Each change reloads, so the list shows what the server kept.
  const then = (work: Promise<unknown>) => work.then(() => {
    channel?.postMessage('changed');
    return load();
  });

  if (hidden) return null;
  return (
    <TimeTrackerButton
      projects={projects}
      punches={punches}
      // The picker starts on the project timed last.
      {...(punches[0] ? { defaultProjectId: punches[0].projectId } : {})}
      loading={loading}
      error={error}
      footnote={`The last ${DAYS} days`}
      onOpenChange={(open) => {
        if (open) void load();
      }}
      onStart={(projectId) => then(startTimer(authenticatedFetch, projectId))}
      onStop={() => then(stopTimer(authenticatedFetch))}
      onSave={(punchId, times) => then(updatePunch(authenticatedFetch, punchId, { started_at: times.startedAt, ended_at: times.endedAt }))}
      onDelete={(punchId) => then(deletePunch(authenticatedFetch, punchId))}
    />
  );
}
